import { useEffect, useMemo, useRef } from "react";

import { hasAnnotationOverlay } from "./annotationTargetContent";
import type {
  AnnotationOperationState,
  AnnotationSetDraft,
  AnnotationStudioController,
  AnnotationTarget,
} from "./types";

// 디바운스는 사용자가 점을 연달아 찍는 동안 매 점마다 segment를 쏘지 않기 위한 장치다.
const INTERACTIVE_SEGMENT_DEBOUNCE_MS = 280;

interface PendingSegmentationTarget {
  readonly frameId: string;
  readonly targetId: string;
  readonly pointRevision: number;
}

function isHydratedBackfillTarget(
  hydrationDraft: AnnotationSetDraft | null,
  frameId: string,
  targetId: string,
  pointRevision: number,
): boolean {
  const target = hydrationDraft?.frames[frameId]?.targets[targetId];
  return (
    target !== undefined &&
    target.pointRevision === pointRevision &&
    target.points.length > 0 &&
    !hasAnnotationOverlay(target)
  );
}

export function useAnnotationSegmentation({
  controller,
  enabled,
  draft,
  targets,
  operation,
}: {
  controller: AnnotationStudioController;
  enabled: boolean;
  draft: AnnotationSetDraft | null;
  targets: readonly AnnotationTarget[];
  operation: AnnotationOperationState;
}) {
  const draftFrames = draft?.frames;
  const activeFrameId = draft?.activeFrameId;

  // 초기 draft 자체가 hydrate 시점의 immutable 기준선이다. 전체 frame/target을 문자열 Set으로
  // 복제하지 않고 pending target만 O(1)로 대조해 backfill과 이후 점 편집을 구분한다.
  const hydrationDraftRef = useRef<{
    draftId: string | null;
    draft: AnnotationSetDraft | null;
  }>({
    draftId: null,
    draft: null,
  });
  if (draft === null) {
    if (hydrationDraftRef.current.draft !== null) {
      hydrationDraftRef.current = { draftId: null, draft: null };
    }
  } else if (hydrationDraftRef.current.draftId !== draft.draftId) {
    hydrationDraftRef.current = { draftId: draft.draftId, draft };
  }

  const pendingTargets = useMemo<readonly PendingSegmentationTarget[] | null>(() => {
    if (
      !enabled ||
      !draftFrames ||
      activeFrameId === null ||
      activeFrameId === undefined ||
      operation.error !== null
    ) {
      return null;
    }
    const frame = draftFrames[activeFrameId];
    if (!frame) return null;
    const candidates: PendingSegmentationTarget[] = [];
    for (const target of targets) {
      const targetDraft = frame.targets[target.id];
      if (!targetDraft || targetDraft.points.length === 0 || hasAnnotationOverlay(targetDraft)) {
        continue;
      }
      candidates.push({
        frameId: activeFrameId,
        targetId: target.id,
        pointRevision: targetDraft.pointRevision,
      });
    }
    return candidates.length > 0 ? candidates : null;
  }, [activeFrameId, draftFrames, enabled, operation.error, targets]);

  useEffect(() => {
    if (!enabled || !pendingTargets || operation.pending || operation.error !== null) {
      return;
    }
    const firstTarget = pendingTargets[0];
    if (!firstTarget) return;
    const isBackfill = pendingTargets.every(({ frameId, targetId, pointRevision }) =>
      isHydratedBackfillTarget(hydrationDraftRef.current.draft, frameId, targetId, pointRevision),
    );
    const timeout = window.setTimeout(
      () => {
        if (pendingTargets.length > 1 && controller.segmentTargets) {
          void controller.segmentTargets(
            firstTarget.frameId,
            pendingTargets.map(({ targetId }) => targetId),
          );
          return;
        }
        void controller.segmentTarget(firstTarget.frameId, firstTarget.targetId);
      },
      isBackfill ? 0 : INTERACTIVE_SEGMENT_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [controller, enabled, operation.error, operation.pending, pendingTargets]);

  return {
    pendingTargets,
    queuedOrPending: pendingTargets !== null || operation.pending,
  };
}
